import styled from '@emotion/styled'
import { AnimatedImage } from 'app/components/Common/Animation/AnimatedImage'
import { Close } from 'app/components/Common/Close'
import { Gallery } from 'app/components/Common/Gallery'
import { Image, Props as ImageProps } from 'app/components/Common/Image'
import { styles } from 'app/theme'
import useLockedBody from 'app/utils/useLockedBody'
import { uniq } from 'lodash'
import React, { memo, useEffect, useState } from 'react'

export interface Props {
  images: ImageProps[]
  languageCode: string
  thumbnails?: ImageProps[]
}

export const Mosaic = memo(function Mosaic({
  images,
  languageCode,
  thumbnails,
}: Props) {
  if (images?.length < 1) {
    return null
  }

  const [_locked, setLocked] = useLockedBody()
  const [modalGalleryStatus, setModalGalleryStatus] = useState(false)
  const [clickedSlide, setClickedSlide] = useState<any>(null)

  useEffect(() => {
    if (modalGalleryStatus) {
      document.addEventListener('keydown', function (e) {
        switch (e.key) {
          case 'Escape':
            setLocked(false)
            setModalGalleryStatus(false)
            setTimeout(() => {
              setClickedSlide(0)
            }, 1200)
            break
        }
      })
    }
  }, [modalGalleryStatus])

  return (
    <Container>
      <Wrapper>
        {uniq(thumbnails).map((item, index) => (
          <Item
            counter={index}
            delay={index * 0.15}
            key={index}
            onClick={(e: any) => {
              if (!modalGalleryStatus) {
                setLocked(true)
                setModalGalleryStatus(true)
              } else {
                setLocked(false)
                setModalGalleryStatus(false)
              }

              const realClickedSlide = Number(
                e.currentTarget.getAttribute('data-counter'),
              )

              setClickedSlide(realClickedSlide)
              setTimeout(() => {
                setClickedSlide(null)
              }, 100)
            }}
          >
            <Image {...item} />
          </Item>
        ))}
      </Wrapper>

      <Modal className={modalGalleryStatus ? 'open' : undefined}>
        <Close
          languageCode={languageCode}
          onClick={() => {
            setLocked(false)
            setModalGalleryStatus(false)

            setTimeout(() => {
              setClickedSlide(0)
            }, 1200)
          }}
        />
        <Gallery
          clickedSlide={clickedSlide}
          images={images}
          isOpen={modalGalleryStatus}
        />
      </Modal>
    </Container>
  )
})

const Container = styled.section`
  margin-top: 11.25rem;
  padding: 0 5vw 4.5rem;

  @media (max-width: 1199px) {
    margin-top: 5.25rem;
    padding: 0 2rem 2rem;
  }
`

const Wrapper = styled.div`
  display: grid;
  grid-gap: 3.333vw;
  grid-template-columns: repeat(4, 1fr);
  grid-auto-rows: 20vw 3.333vw 20vw;
  grid-auto-flow: dense;

  > div {
    &:nth-of-type(4n + 1),
    &:nth-of-type(8n + 3),
    &:nth-of-type(8n + 6) {
      grid-row: span 2;
    }
  }

  @media (max-width: 1199px) {
    grid-gap: 2rem;
    grid-template-columns: repeat(2, 1fr);
    grid-auto-rows: 10.4375rem 1.4375rem 10.4375rem;

    > div {
      &:nth-of-type(4n + 1),
      &:nth-of-type(8n + 3),
      &:nth-of-type(8n + 6) {
        grid-row: unset;
      }
      &:nth-child(odd) {
        grid-row: span 2 !important;
      }
    }
  }
`

const Item = styled(AnimatedImage)`
  height: 100%;
  cursor: pointer;
  overflow: hidden;
  position: relative;
  &:hover {
    img {
      transform: none;
    }
  }

  img {
    ${styles.img}

    transition: 1s cubic-bezier(0.25, 1, 0.5, 1);
    transform: scale(1.1);
  }
`

const Modal = styled.div`
  width: 100%;
  height: 100%;
  background: ${({ theme }) => theme.colors.variants.neutralLight1};
  opacity: 0;
  overflow-y: auto;
  position: fixed;
  top: 0;
  left: 0;
  visibility: hidden;
  z-index: -1;
  transition: 0.2s ease-out;
  -webkit-overflow-scrolling: auto;
  &.open {
    opacity: 1;
    visibility: visible;
    z-index: 10002;
  }
`
